import { useId } from "react";
import type { ReportCard } from "../../trainer/reportCard.ts";
import { readingFacts } from "../../trainer/positionReading.ts";
import { ModalDialog } from "../layout/ModalDialog.tsx";
import { MedalIcon } from "./Scorecard.tsx";
import { ordinal } from "../../trainer/medals.ts";

type ReportCardMode = "live" | "review";

function Ornament() {
  return (
    <svg className="rc-ornament" viewBox="0 0 120 12" aria-hidden="true" focusable="false">
      <line x1="0" y1="6" x2="48" y2="6" stroke="currentColor" strokeWidth="0.8" />
      <path d="M54 6 L60 1 L66 6 L60 11 Z" fill="currentColor" />
      <line x1="72" y1="6" x2="120" y2="6" stroke="currentColor" strokeWidth="0.8" />
    </svg>
  );
}

function Seal({ word, score }: { word: string; score: number }) {
  const radius = 46;
  const circumference = 2 * Math.PI * radius;
  const dash = (Math.max(0, Math.min(100, score)) / 100) * circumference;
  return (
    <div className={`rc-seal rc-seal-${word.toLowerCase()}`} aria-hidden="true">
      <svg viewBox="0 0 110 110" focusable="false">
        <circle cx="55" cy="55" r="52" className="rc-seal-outer" />
        <circle cx="55" cy="55" r={radius} className="rc-seal-track" />
        <circle
          cx="55"
          cy="55"
          r={radius}
          className="rc-seal-arc"
          strokeDasharray={`${dash} ${circumference - dash}`}
          transform="rotate(-90 55 55)"
        />
        <circle cx="55" cy="55" r="38" className="rc-seal-inner" />
      </svg>
      <span className="rc-seal-word">{word}</span>
    </div>
  );
}

function Sparkline({ card }: { card: ReportCard }) {
  const { points } = card.trend;
  const width = 320;
  const height = 72;
  const padX = 18;
  const padY = 12;
  if (!card.trend.available || points.length < 2) {
    return (
      <p className="rc-trend-empty" role="img" aria-label={card.trend.summary}>
        {card.trend.summary}
      </p>
    );
  }
  // Keep mate information in the model; only the drawing has a capped scale.
  const plotCp = (cp: number) => Math.max(-1500, Math.min(1500, cp));
  const values = points.map((p) => plotCp(p.cp));
  const max = Math.max(...values, 0);
  const min = Math.min(...values, 0);
  const span = Math.max(60, max - min);
  const x = (index: number) => padX + (index * (width - padX * 2)) / (points.length - 1);
  const y = (cp: number) => height - padY - ((plotCp(cp) - min) / span) * (height - padY * 2);
  const path = points
    .map((p, index) => `${index === 0 ? "M" : "L"}${x(index).toFixed(1)} ${y(p.cp).toFixed(1)}`)
    .join(" ");
  const area = `${path} L${x(points.length - 1).toFixed(1)} ${(height - padY).toFixed(1)} L${x(0).toFixed(1)} ${(height - padY).toFixed(1)} Z`;
  const zero = y(0);
  const format = (cp: number) =>
    Math.abs(cp) >= 50_000
      ? cp > 0
        ? "Mate +"
        : "Mate −"
      : Math.abs(cp) >= 1500
        ? cp > 0
          ? "+15+"
          : "−15+"
        : `${cp > 0 ? "+" : ""}${(cp / 100).toFixed(1)}`;
  return (
    <figure className="rc-sparkline">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={card.trend.summary}
        focusable="false"
      >
        <line x1={padX} x2={width - padX} y1={zero} y2={zero} className="rc-spark-zero" />
        <path d={area} className="rc-spark-area" />
        <path d={path} className="rc-spark-line" />
        {points.map((p, index) => (
          <g key={p.moveNumber}>
            <circle cx={x(index)} cy={y(p.cp)} r="3.2" className="rc-spark-dot" />
            <text x={x(index)} y={height - 1} textAnchor="middle" className="rc-spark-label">
              {p.moveNumber}
            </text>
          </g>
        ))}
        <text x={x(0)} y={y(points[0].cp) - 7} textAnchor="middle" className="rc-spark-value">
          {format(points[0].cp)}
        </text>
        <text
          x={x(points.length - 1)}
          y={y(points[points.length - 1].cp) - 7}
          textAnchor="middle"
          className="rc-spark-value"
        >
          {format(points[points.length - 1].cp)}
        </text>
      </svg>
      <figcaption>{card.trend.summary}</figcaption>
    </figure>
  );
}

export function ReportCardModal({
  card,
  mode,
  onContinue,
  onFinish,
  onLater,
  onClose,
}: {
  card: ReportCard;
  mode: ReportCardMode;
  onContinue: () => void;
  onFinish: () => void;
  onLater: () => void;
  onClose: () => void;
}) {
  const headingId = useId();
  const decision = mode === "live" && card.finalWindow && !card.freePlay;
  const cancel =
    mode === "review" ? onClose : decision ? onLater : card.gameOver ? onFinish : onContinue;
  const facts = readingFacts(card.reading);
  const themeOrder = [...card.themes.achieved, ...card.themes.pending, ...card.themes.missed].sort(
    (a, b) => a.mainlineMove - b.mainlineMove
  );
  let section = 0;
  const next = () => ({ "--i": section++ }) as React.CSSProperties;

  return (
    <ModalDialog
      open
      onClose={cancel}
      labelledBy={headingId}
      className="report-modal"
      id="reportCard"
    >
      <article className="rc" aria-describedby={`${headingId}-headline`}>
        <header className="rc-head">
          <div className="rc-head-text">
            <p className="rc-eyebrow">
              Report card · {card.openingTitle}
              {card.freePlay ? " · free play" : ""}
            </p>
            <h2 id={headingId} className="rc-title">
              {card.title}
            </h2>
            <p id={`${headingId}-headline`} className="rc-headline">
              {card.headline}
            </p>
            <p className="rc-summary">
              <span className="sr-only">{card.grade.word}. </span>
              {card.grade.summary}
            </p>
          </div>
          <Seal word={card.grade.word} score={card.grade.score} />
          <button className="rc-close" type="button" aria-label="Close" onClick={cancel}>
            ×
          </button>
        </header>

        <div className="rc-body" tabIndex={0} role="region" aria-label="Report card details">
          <section
            className="rc-section rc-moves"
            style={next()}
            aria-label={
              card.moves.length === 5 ? "The five moves" : `The ${card.moves.length} moves`
            }
          >
            <ol
              className="rc-move-strip"
              style={{ "--rc-moves": card.moves.length } as React.CSSProperties}
            >
              {card.moves.map((move) => (
                <li
                  key={move.moveNumber}
                  className={`rc-move band-${move.band}${move.inBook ? " in-book" : ""}`}
                  aria-label={`Move ${move.moveNumber}, ${move.san}${move.reply ? (card.playerSide === "b" ? `, answering White's ${move.reply}` : `, Black replied ${move.reply}`) : ""}. ${move.inBook ? `${move.bookLabel || "Book"} move. ` : ""}${move.bandLabel}${move.medal ? `, ${move.medal} medal` : ""}${move.rank != null && move.rankTotal ? `, ranked ${move.rank} of ${move.rankTotal}` : ""}${move.assisted ? ", assisted" : ""}.`}
                >
                  <span className="rc-move-number">{move.moveNumber}</span>
                  <span className="rc-move-san">{move.san}</span>
                  {move.inBook ? (
                    <span className="rc-move-book" title={move.bookLabel || "Book"}>
                      {move.bookLabel === "Variation"
                        ? "Branch"
                        : move.bookLabel === "Transposed"
                          ? "Rejoined"
                          : move.bookLabel || "Book"}
                    </span>
                  ) : null}
                  {move.reply ? (
                    <span className="rc-move-reply">
                      {card.playerSide === "b" ? "White: " : "…"}
                      {move.reply}
                    </span>
                  ) : null}
                  <span className="rc-move-band" title={move.bandLabel}>
                    {move.medal ? (
                      <MedalIcon medal={move.medal} />
                    ) : (
                      <span className="rc-move-dot" />
                    )}
                    <span className="rc-move-band-label">{move.bandLabel}</span>
                  </span>
                  {move.rank != null && move.rankTotal ? (
                    <span className="rc-move-rank">
                      {ordinal(move.rank)} / {move.rankTotal}
                    </span>
                  ) : null}
                  {move.assisted ? <span className="rc-move-assisted">assisted</span> : null}
                </li>
              ))}
            </ol>
            {card.moves.some((move) => move.inBook && move.rank != null && move.rank > 3) ? (
              <p className="rc-book-note">
                A prepared move can serve the opening’s plan without ranking first. Your book
                knowledge and Stockfish’s ranking are separate measures.
              </p>
            ) : null}
          </section>

          <section className="rc-section rc-trend" style={next()} aria-label="Engine trend">
            <h3>
              <span className="rc-h-eyebrow">The engine's view</span>
              <span className="rc-h-title">How the evaluation moved</span>
            </h3>
            <Sparkline card={card} />
          </section>

          <div className="rc-grid">
            <section
              className="rc-section rc-card rc-strengths"
              style={next()}
              aria-label="What went well"
            >
              <h3>
                <span className="rc-h-eyebrow">Keep</span>
                <span className="rc-h-title">What went well</span>
              </h3>
              <ul>
                {card.strengths.map((item) => (
                  <li key={item.title}>
                    <strong>{item.title}.</strong> {item.detail}
                  </li>
                ))}
              </ul>
            </section>
            <section
              className="rc-section rc-card rc-weaknesses"
              style={next()}
              aria-label="What to check"
            >
              <h3>
                <span className="rc-h-eyebrow">Check</span>
                <span className="rc-h-title">What needs attention</span>
              </h3>
              <ul>
                {card.weaknesses.map((item) => (
                  <li key={item.title}>
                    <strong>{item.title}.</strong> {item.detail}
                  </li>
                ))}
              </ul>
            </section>
          </div>

          <section className="rc-section rc-reflect" style={next()} aria-label="Think about">
            <Ornament />
            <h3>
              <span className="rc-h-eyebrow">A breather</span>
              <span className="rc-h-title">Think about these before you return</span>
            </h3>
            <ol className="rc-reflect-list">
              {card.reflections.map((line, index) => (
                <li key={`${index}-${line.slice(0, 24)}`}>{line}</li>
              ))}
            </ol>
          </section>

          <section className="rc-section rc-reading" style={next()} aria-label="The position now">
            <h3>
              <span className="rc-h-eyebrow">The board</span>
              <span className="rc-h-title">The position as it stands</span>
            </h3>
            <dl className="rc-facts">
              {facts.map((fact) => (
                <div key={fact.label} className="rc-fact">
                  <dt>{fact.label}</dt>
                  <dd>{fact.value}</dd>
                </div>
              ))}
            </dl>
            <p className="rc-reading-prose">{card.reading.sentences.slice(0, 5).join(" ")}</p>
          </section>

          {card.themes.total > 0 ? (
            <section className="rc-section rc-themes" style={next()} aria-label="Course ideas">
              <h3>
                <span className="rc-h-eyebrow">The course</span>
                <span className="rc-h-title">
                  {card.themes.done} of {card.themes.total} ideas on the board
                </span>
              </h3>
              <ul className="rc-theme-list">
                {themeOrder.map((theme) => (
                  <li
                    key={theme.id}
                    className={`rc-theme rc-theme-${theme.state}${card.themes.newlyAchieved.some((t) => t.id === theme.id) ? " is-new" : ""}`}
                    title={theme.idea}
                  >
                    <span className="rc-theme-mark" aria-hidden="true">
                      {theme.state === "done" ? "✓" : theme.state === "missed" ? "–" : "○"}
                    </span>
                    <span className="rc-theme-title">{theme.title}</span>
                    <span className="sr-only">
                      {theme.state === "done"
                        ? ", achieved"
                        : theme.state === "missed"
                          ? ", no longer available"
                          : ", still ahead"}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          <section
            className="rc-section rc-next"
            style={next()}
            aria-label={card.gameOver ? "After the game" : "The next five moves"}
          >
            <h3>
              <span className="rc-h-eyebrow">Next</span>
              <span className="rc-h-title">{card.nextFive.headline}</span>
            </h3>
            <ul className="rc-next-list">
              {card.nextFive.steps.map((step, index) => (
                <li key={`${index}-${step.slice(0, 24)}`}>{step}</li>
              ))}
            </ul>
            <p className="rc-habit">
              <span className="rc-habit-label">
                {card.gameOver ? "One habit to take with you" : "One habit for the next five"}
              </span>
              {card.nextFive.habit}
            </p>
          </section>

          <p className="rc-closing" style={next()}>
            {card.closing}
          </p>
        </div>

        <footer className="rc-foot">
          {mode === "review" ? (
            <button className="btn-primary rc-btn" type="button" onClick={onClose}>
              Back to the board
            </button>
          ) : card.gameOver || (card.finalWindow && card.freePlay) ? (
            <button className="btn-primary rc-btn" type="button" onClick={onFinish}>
              See the session recap
            </button>
          ) : decision ? (
            <>
              <button className="btn-primary rc-btn" type="button" onClick={onContinue}>
                Keep playing into the middlegame
              </button>
              <button className="btn-ghost rc-btn" type="button" onClick={onFinish}>
                Finish and see the recap
              </button>
              <button className="btn-ghost rc-btn rc-btn-quiet" type="button" onClick={onLater}>
                Decide at the board
              </button>
            </>
          ) : (
            <button className="btn-primary rc-btn" type="button" onClick={onContinue}>
              Continue to move {card.toMove + 1}
            </button>
          )}
        </footer>
      </article>
    </ModalDialog>
  );
}
